import { cloneElement, useContext, useEffect, useCallback, Children, useState, useMemo, isValidElement } from 'react';
import { Transition } from '@/components';
import FormContext from './context';
import classnames from 'classnames';

import './form.module.scss';

import type { FC, ReactElement, ReactNode } from 'react';
import type { RulesItem, FormValue, FormErrors } from './useForm';

export interface FormItemProps {

  className?: string;

  /**
   * @description 字段名		
   */
  name?: string;

  /**
  * @description label 标签的文本			
  */
  label?: ReactNode;

  labelProps?: {

    /* 
    * @description
    */
    shrink?: boolean;
  }

  /**
  * @description 校验规则，设置字段的校验逻辑
  */
  rules?: RulesItem[];

  /**
   * @description 子节点
   */
  children: JSX.Element;

  /**
   * @description 子节点的值的属性，如 Switch 的是 'checked'
   */
  valuePropName?: string;

  requiredMark?: boolean | ReactNode;

} 

const FormItem: FC<FormItemProps> = (props) => {

  const { className, name = '', label, rules = [], children, valuePropName = 'value', labelProps: propsLabelProps, requiredMark: propsRequiredMark } = props;
  const prefixCls = 'web_components_form';


  const {
    getFieldsError,
    getFieldsValue,
    setFieldsValue,
    setFieldsError,
    removeFieldErrors,
    setFieldsValidatorRules,
    labelProps: contextLabelProps,
    requiredMark: contextRequiredMark
  } = useContext(FormContext);

  const labelProps = propsLabelProps || contextLabelProps;
  const requiredMark = propsRequiredMark || contextRequiredMark;

  const [isFocus, setIsFocus] = useState<boolean>(!!labelProps?.shrink);

  const values: FormValue = getFieldsValue();
  const errors: FormErrors = getFieldsError();

  const value = values[name];
  const error = errors[name];

  const requiredMarkNode = useMemo(() => {

    if (typeof requiredMark === 'boolean' && requiredMark) {
      return (<span className='required_mark'>*</span>)
    }
    if (isValidElement(requiredMark)) {
      return requiredMark
    }
    return null;
  }, [requiredMark, isFocus])

  const isExistRequiredFields = useMemo(() => {
    return rules?.some((ruleItem: RulesItem) => ruleItem.required)
  }, [rules])

  useEffect(() => {
    if (name && rules.length) {
      setFieldsValidatorRules({ [name]: rules });
    }
  }, [name, JSON.stringify(rules)]);

  useEffect(() => {
    if (labelProps?.shrink) {
      setIsFocus(true);
    }
  }, [labelProps?.shrink]);




  useEffect(() => {
    synchronousForm();
  }, [value, name]);

  const isValidRequired = (fieldRules: RulesItem) => {
    return fieldRules.required && !value;
  }

  const isValidPattern = (fieldRules: RulesItem) => {
    if (!isExistRequiredFields && !value) return;

    if (fieldRules.pattern) {
      if (fieldRules.pattern instanceof RegExp) {
        return !fieldRules.pattern?.test(value);
      }
      if (typeof fieldRules.pattern === 'function') {
        return fieldRules.pattern(value);
      }
    }
  }

  const isValidMax = (fieldRules: RulesItem) => {
    return fieldRules.max && value?.length > Number(fieldRules.max)
  }

  const isValidMin = (fieldRules: RulesItem) => {
    return fieldRules.min && value?.length > Number(fieldRules.min)
  }

  const synchronousForm = () => {

    if (!rules.length || !name) return;

    // 是否触发过 
    const __isEdit = value !== undefined;
    if (!__isEdit) return;

    let isError = false;

    for (let i = 0; i < rules.length; i++) {
      const fieldRules = rules[i];

      if (isValidRequired(fieldRules)) {
        isError = true;
        setFieldsError({ [name]: [{ message: fieldRules.message || 'Required' }] });
        break;
      }

      if (isValidPattern(fieldRules)) {
        isError = true;
        setFieldsError({ [name]: [{ message: fieldRules.message || 'Error' }] });
        break;
      }

      if (isValidMax(fieldRules)) {
        isError = true;
        setFieldsError({ [name]: [{ message: fieldRules.message || 'Error' }] });
        break;
      }

      if (isValidMin(fieldRules)) {
        isError = true;
        setFieldsError({ [name]: [{ message: fieldRules.message || 'Error' }] });
        break;
      }
    }

    if (!isError && error) {
      removeFieldErrors(name);
    }
  }

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | any) => {
      let newValue = null;
      // 如果是传统控件。value 一般在event上挂载
      if (event?.target) {
        newValue = event.target[valuePropName];
      } else {
        newValue = event;
      }
      setFieldsValue({ [name]: newValue });
    },
    [setFieldsValue]
  );

  const handleFocus = () => {
    setIsFocus(true);
  }

  const handleBlur = () => {
    if (!labelProps?.shrink) {
      setIsFocus(false);
    }
  }

  const getRulesProps = () => {
    const rulesProps: Record<string, any> = {};

    for (let i = 0; i < rules.length; i++) {
      if (rules[i].max !== undefined) {
        if (typeof rules[i].max === 'number') {
          rulesProps.max = rules[i].max;
        }
        if (typeof rules[i].max === 'string') {
          rulesProps.maxLength = Number(rules[i].max);
        }

        if (typeof rules[i].min === 'number') {
          rulesProps.min = rules[i].max;
        }
        if (typeof rules[i].min === 'string') {
          rulesProps.minLength = Number(rules[i].max);
        }
      }
    }
    return rulesProps;
  }

  const getConformRulesValue = (value: any, propsValue: any) => {
    if (value !== undefined && value !== null) {
      return value;
    }

    if (propsValue !== undefined && propsValue !== null) {
      return propsValue
    }

    return ''

  }

  const modifiedChildren = Children.map(children, (child: ReactElement) => {
    const childProps = child.props || {};

    const modifiedProps = {
      ...getRulesProps(),
      ...childProps,
      [valuePropName]: getConformRulesValue(values[name], childProps.value),
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event);
        childProps?.onChange?.(event);
      },
      onFocus: (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFocus();
        childProps?.onFocus?.(event);
      },
      onBlur: (event: React.ChangeEvent<HTMLInputElement>) => {
        handleBlur();
        childProps?.onBlur?.(event);
      },
      status: error && 'error',
      className: classnames(`${prefixCls}_widget`, childProps.className)
    };

    return cloneElement(child, modifiedProps, child.props.children);

  });

  if (!name) {
    return (<div className={`${prefixCls}_item`}>{children}</div>)
  }

  return (
    <div
      className={
        classnames(
          `${prefixCls}_item`,
          {
            [`${prefixCls}_item_widget_focus`]: (isFocus || value || String(value) === '0') && label,
            [`${prefixCls}_item_widget_focus_default`]: label,
            [`${prefixCls}_item_has_error`]: !!error
          },
          className
        )
      }>
      <div className={`${prefixCls}_item_content`} tabIndex={-1}>
        {label &&
          <label
            className={`${prefixCls}_label`}
            htmlFor={name}>
            {label}
            {requiredMarkNode}
          </label>}
        {modifiedChildren}
      </div>
      <Transition isOpen={!!error} cache duration={200}>
        <div className={`${prefixCls}_error_text`}>{error?.[0]?.message}</div>
      </Transition>
    </div>
  );
};

export default FormItem;
